import React, { useState, useEffect, useCallback, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useBeforeunload } from 'react-beforeunload';
import { useForm, useFormState } from 'react-hook-form';
import styled, { createGlobalStyle } from 'styled-components';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { isArray, isEmpty, intersection, cloneDeepWith } from 'lodash';
import {
  HrWhite,
  HrImageContainer,
  HrFlexContainer,
  HrlightestGrey,
  HrPrimary,
  HrButtons,
  HrSecondary,
  HrIconButton,
  HrParagraph,
  HrDarkGrey,
  Tablet,
  HrTitleH6
} from '@components/styles/hr-styles';
import Turnstile, { useTurnstile } from 'react-turnstile';
import { HrButton } from '@components/button/button';
import { useMutation } from '@tanstack/react-query';
import useStore from '../store/useStore';
import { Tabs } from '../components/tabs';
import { Header } from '../components/header';
import { useQuery } from '../../hook/use-query';
import Toast from '../../pipeline/components/Toast';
import { ModalFaqs } from '../components/modals/faqs';
import { ModalLogin } from '../components/modals/login';
import { ModalSupport } from '../components/modals/support';
import { StepImportCv } from '../components/step-1-import-cv';
import { ConfirmExit } from '../components/modals/confirm-exit';
import { UpdateProfile } from '../components/modals/update-profile';
import { MiniModalSupport } from '../components/modals/mini-support';
import { SuccessfulUpdate } from '../components/modals/successful-update';
import { ApplicationInformation } from '../components/applicationInformation';
import {
  mapPostulantApiNavent,
  getPostulantSource,
  formatEducationList,
  formatJobsList,
  isFormSubmitPending
} from '../utils/postulant';
import { SuccessfulApplication } from '../components/modals/successful-application';
import { StepPersonalInformation } from '../components/step-2-personal-information';
import { createPostulant, getPostulant, createBumeranPerfil } from '../services/api';
import { StepEducationWorkExperience } from '../components/step-3-education-work-experience';
import { useRouter } from '../../hook/use-router';
import { ModalShowErrors } from '../components/modals/show-errors';

import { DisplaySettingsContext } from '../store/display-settings';

const GeneralContainer = createGlobalStyle`
  body {
    margin: 0;
    background-color: ${(props) => props.backgroundColor || 'transparent'};
  }
`;

const Flex = styled(HrFlexContainer)`
  padding: 0 32px;

  &.mobile {
    display: none;
  }

  @media (max-width: ${Tablet}) {
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    &.desktop {
      display: none;
    }

    &.mobile {
      display: flex;
    }
  }
`;

const maxWidth = '1140px';

const stepOneRequired = [
  'fotoURL',
  'foto_perfil',
  'firstname',
  'lastname',
  'email',
  'dni',
  'type_doc',
  'prefix_phone_1',
  'phone',
  'type_phone_1',
  'prefix_phone_2',
  'phone_2',
  'type_phone_2',
  'kudertActivo',
  'gender',
  'nationality',
  'location',
  'permanent_address',
  'born',
  'bornObj',
  'discapacidad',
  'emailConfirm'
];

export const PostulantCreate = observer(({ cloudflareRecaptcha }) => {
  useRouter();
  const navigate = useNavigate();
  const location = useLocation();
  const [t] = useTranslation();
  const _query = useParams();
  const _queryParams = useQuery();
  const source = _queryParams.get('source');
  // attr solo para postulantes provenientes del portal gratis talent
  const clickId = _queryParams.get('clickid');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [postulant, setPostulant] = useState({});
  const [showModalSuport, setShowModalSuport] = useState(false);
  const [showMiniModalSuport, setShowMiniModalSuport] = useState(false);
  const [showModalFaqs, setShowModalFaqs] = useState(false);
  const [showModalUpdateProfile, setShowModalUpdateProfile] = useState(false);
  const [showModalUpdateProfileSuccessful, setShowModalUpdateProfileSuccessful] = useState(false);
  const [showModalUpdateProfileApplication, setShowModalUpdateProfileApplication] = useState(false);
  const [showModalExit, setShowModalExit] = useState(false);
  const [showModalShowErrors, setShowModalShowErrors] = useState(false);
  const [showModalLogin, setShowModalLogin] = useState({
    show: false,
    portal: '',
    url: ''
  });
  const [toastState, setToastState] = useState({
    visible: false,
    message: null,
    typeToast: null
  });

  const [isPendingRecaptchaGeneration, setIsPendingRecaptchaGeneration] = useState(false);

  const theme = {
    primaryColor: HrPrimary,
    secondaryColor: HrWhite,
    tertiaryColor: HrlightestGrey,
    textSpecialColor: HrPrimary,
    textRegularColor: HrSecondary
  };
  const {
    register,
    handleSubmit,
    formState,
    control,
    clearErrors,
    setValue,
    setError,
    getValues,
    unregister,
    trigger
  } = useForm({
    mode: 'onTouched',
    delayError: 800
  });
  const {
    errors,
    submitCount,
    dirtyFields,
    isSubmitSuccessful,
    isSubmitting,
    isSubmitted
  } = useFormState({
    control
  });
  const {
    vacancy,
    currentStep,
    getDataPostulant,
    vacancyRequirement,
    questions,
    alertaEmpleo
  } = useStore();

  useBeforeunload(async (event) => {
    if (!isEmpty(dirtyFields) && !(isSubmitSuccessful && isSubmitted)) {
      event.preventDefault();
    }
  });
  const turnstile = useTurnstile();

  const onShowToast = (newToastState) => {
    setToastState(newToastState);
  };

  const onCloseToast = () => {
    setToastState({
      visible: false,
      message: null,
      typeToast: null
    });
  };

  const toastError = async (data) => {
    // event.preventDefault();
    const dataError = {};

    try {
      // find key firt chart is name

      const nameFiles = Object.keys(data).find((key) => key.startsWith('loadingfiles__'));
      // [key] === '');
      if (nameFiles) {
        setToastState({
          visible: true,
          type: 'warning',
          message: t(`postulant.error.loadingFile`)
        });
        return;
      }
      const keyErrores = [];
      Object.keys(data).forEach((key) => {
        keyErrores.push(key);
      });
      const priorityError = intersection(keyErrores, stepOneRequired);
      setTimeout(async () => {
        const firstField = priorityError.length > 0 ? priorityError[0] : Object.keys(data)[0];
        if (firstField) {
          await trigger(firstField);
        }
      }, 300);

      Object.keys(data).forEach((key) => {
        if (Array.isArray(data[key])) {
          data[key].forEach((item, index) => {
            const position = index + 1;
            if (typeof item === 'object') {
              Object.keys(item).forEach((keyItem) => {
                dataError[`${position}-${key}-${keyItem}`] = item[keyItem]?.message;
              });
            }
          });
        } else if (key === 'location' || key === 'bornObj') {
          Object.keys(data[key]).forEach((key2) => {
            dataError[`${key}-${key2}`] = data[key][key2]?.message;
          });
        } else {
          dataError[key] = data[key]?.message;
        }
      });

      const errorString = JSON.stringify(dataError);
      sessionStorage.setItem('errorPage', errorString);
      onShowToast({
        visible: true,
        type: 'error',
        message: `Faltan completar campos obligatorios`
      });
    } catch (error) {
      // session set error
      sessionStorage.setItem('errorPage', JSON.stringify(error.message) || 'ocurrio un error');
      onShowToast({ visible: true, type: 'error', message: `Ocurrio un error` });
    }
  };

  const _next = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    const _step = currentStep >= 2 ? 3 : currentStep + 1;
    navigate({
      pathname: location.pathname,
      replace: true,
      search: location.search,
      state: { currentStep: _step },
      hash: `#step${_step}`
    });
  };

  const _prev = () => {
    if (currentStep === 2 || currentStep === 3) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      // window.scrollTo(0, 0);
    }
    const _step = currentStep - 1;
    navigate({
      pathname: location.pathname,
      replace: true,

      search: location.search,
      hash: `#step${_step}`,
      state: { currentStep: _step }
    });
  };

  const validateErrorsSteps = (errorss) => {
    const nameFiles = Object.keys(errorss).find((key) => key.startsWith('loadingfiles__'));
    if (nameFiles) {
      setToastState({ visible: true, type: 'warning', message: t(`postulant.error.loadingFile`) });
      return;
    }
    const arr = [];
    Object.keys(errorss).forEach((key) => {
      arr.push(key);
    });
    const validateStep1 = intersection(arr, stepOneRequired);
    if (validateStep1.length > 0) {
      navigate({
        pathname: location.pathname,
        replace: true,

        search: location.search,
        state: { currentStep: 2 },
        hash: `#step${2}`
      });
    }
  };

  const createInvitationForBumeran = useMutation(createBumeranPerfil);

  const resetWidgets = () => {
    if (cloudflareRecaptcha) {
      turnstile.reset();
    } else {
      setIsPendingRecaptchaGeneration(false);
    }
  };

  const getPostulantExternal = useMutation(getPostulant, {
    onSuccess: (data) => {
      if (data.data.result === 'success') {
        const post = mapPostulantApiNavent(data.data.response, 'create', vacancyRequirement);
        setPostulant(post);
        Object.keys(post).forEach((key) => {
          const _data = post[key];
          if (_data && !isArray(_data)) {
            setValue(key, post[key]);
          }
        });
        setValue('conocimientosDesnormalizados', post.conocimientosDesnormalizados || []);
        setValue('conocimientosNormalizados', post.conocimientosNormalizados || []);
        setValue('referencias', post.referencias || []);
        navigate({
          pathname: location.pathname,
          replace: true,
          search: location.search,
          state: { currentStep: 2 },
          hash: `#step${2}`
        });
        trigger();
      }
      if (data.data.result === 'error') {
        let messageError = t(`postulant.error.${data.data.response}`);
        let typeError = 'error';
        if (messageError === `postulant.error.${data.data.response}`) {
          messageError = t(`postulant.error.support`);
        }
        if (messageError === t(`postulant.error.postulant_exists`)) {
          typeError = 'warning';
        }
        onShowToast({ visible: true, type: typeError, message: messageError });
      }
      if (data.data.statusCode === 404)
        onShowToast({ visible: true, type: 'error', message: data.data.response });
    },
    onError: (error) => {
      onShowToast({ visible: true, type: 'error', message: error });
    }
  });

  const createPostulation = useMutation(createPostulant, {
    onSuccess: (data) => {
      resetWidgets();
      if (data.status === 201) {
        setShowModalUpdateProfileApplication(true);
        // Aquí va la llamada a bumeran
        const bumeranCheck = getValues('termsConditions.bumeran');
        if (bumeranCheck) {
          const emailPostulant = getValues('email');
          const nationality = getValues('nationality') ?? null;
          const residenceCountry = getValues('location.country') ?? null;

          createInvitationForBumeran.mutate({
            account: vacancy?.account,
            txtEmail: emailPostulant,
            select_postul_country: residenceCountry,
            select_postul_nationality: nationality
          });
        }
      }
      if (data.status !== 201) {
        let messageError = t(`postulant.error.${data.data.response}`);
        let typeError = 'error';
        if (messageError === `postulant.error.${data.data.response}`) {
          messageError = t(`postulant.error.support`);
        }
        if (messageError === t(`postulant.error.postulant_exists`)) {
          typeError = 'warning';
        }
        onShowToast({ visible: true, type: typeError, message: messageError });
      }
      if (data.data.statusCode === 401)
        onShowToast({ visible: true, type: 'error', message: data.data.response });
    },
    onError: (error) => {
      resetWidgets();
      let typeError = 'error';
      let messageKey = 'postulant.error.support';

      if (error.response.status === 500) {
        messageKey = 'postulant.error.support';
      } else {
        const { key } = error.response.data.response;
        messageKey = `postulant.error.${key}`;

        if (messageKey === 'postulant.error.postulant_exists') {
          typeError = 'warning';
        }
      }
      sessionStorage.setItem('ErrorBack', error);
      sessionStorage.setItem('keyErrorBack', error.response.data.response.key);

      const message = t(messageKey);
      onShowToast({ visible: true, type: typeError, message });
    }
  });

  const onSubmitCreate = (dataPre) => {
    const data = cloneDeepWith(dataPre, (p) => (typeof p === 'string' ? p.trim() : undefined));
    if (data.languages) {
      data.languages = data.languages.filter((lang) => lang.language);
    }

    let educationList = data?.educations;
    if (Array.isArray(educationList)) {
      educationList = formatEducationList(educationList, 'new');
      data.educations = JSON.stringify(educationList);
    }

    let jobsList = data.posts;
    if (Array.isArray(jobsList)) {
      jobsList = formatJobsList(jobsList, 'new');
      data.posts = JSON.stringify(jobsList);
    }

    data.gender = data.gender?.toLowerCase();
    data.action = 'new';
    data.alertaEmpleos = alertaEmpleo;

    if (data.desiredSalary && data.desiredSalary <= 0 && vacancyRequirement.remuneration !== 0) {
      onShowToast({ visible: true, type: 'error', message: 'El salario no es válido' });
      return;
    }
    if (vacancy.req_salaryType) {
      data.salaryType = vacancy.req_salaryType;
    } else {
      data.salaryType = null;
    }

    // Parámetros de micrositios
    if (_query.micrositeName) {
      if (
        typeof getDataPostulant.microsites.micrositeId !== 'undefined' &&
        getDataPostulant.microsites.micrositeId
      ) {
        data.microsite_id = getDataPostulant.microsites.micrositeId;
        data.is_multiple = true;
      } else {
        throw new Error(`Microsite param is invalid - ${getDataPostulant.microsites.micrositeId}`);
      }
    }

    // Parámetros del source
    const { typePortal } = _query;
    data.source = getPostulantSource(source, typePortal);

    if (clickId) {
      data.clickId = clickId;
    }

    // Moneda de la postulacion
    data.currency = vacancy.currency;

    // Panel referidos - Parámetro 'ref'
    if (data.source === 'referral') {
      // Panel de referentes
      const referralId = _queryParams.get('ref');
      if (typeof referralId !== 'undefined' && referralId) {
        data.source = 'referral';
        data.ref = referralId;
      } else {
        throw new Error(`Required param 'ReferralId' is invalid - ${referralId} `);
      }
    }

    setIsPendingRecaptchaGeneration(true);
    createPostulation.mutate(data);
  };

  const clickHandlerExecuteRecaptcha = useCallback(
    async (data) => {
      if (!executeRecaptcha) {
        return;
      }

      if (!data.cloudflare_recaptcha_token) {
        const result = await executeRecaptcha('postulation_form');
        data.recaptcha = result;
      }

      onSubmitCreate(data);
    },
    [executeRecaptcha]
  );

  useEffect(() => {
    setValue('vacancy', _query.vacancyId);
    if (
      !getDataPostulant.postulationOptions?.bumeran.allowPostulation &&
      !getDataPostulant.postulationOptions?.zonaJobs.allowPostulation
    ) {
      navigate({
        pathname: location.pathname,
        replace: true,

        search: location.search,
        state: { currentStep: 2 },
        hash: `#step${2}`
      });
    }
  }, []);

  useEffect(() => {
    if (submitCount === 3) {
      setShowMiniModalSuport(true);
    }

    if (submitCount) {
      validateErrorsSteps(errors);
    }
  }, [submitCount]);

  // TODO: Contemplar a la hora de unificar la creacion y edicion unificar la logica de importar CV
  useEffect(() => {
    if (
      showModalLogin.autoLogin ||
      (getDataPostulant.bumeran && showModalLogin.portal === 'bumeran') ||
      (getDataPostulant.zonajobs && showModalLogin.portal === 'zonajobs')
    ) {
      navigate({
        pathname: location.pathname,
        replace: true,

        search: location.search,
        state: { currentStep: 2 },
        hash: `#step${2}`
      });

      getPostulantExternal.mutate({
        apply_with: showModalLogin.apply_with,
        source: showModalLogin.portal,
        token: showModalLogin.token
      });
      setValue('apply_with', showModalLogin.apply_with);
    }
  }, [getDataPostulant, showModalLogin]);

  const {
    language,
    legacy,
    relocation,
    jobs,
    known,
    remuneration,
    salario,
    school,
    educ,
    vacancy_id: vacancyId,
    attached_file: attachedFile,
    available_time: availableTime,
    social_networks: socialNetworks
  } = vacancyRequirement;

  const arrRequeriment = [
    language,
    legacy,
    relocation,
    jobs,
    known,
    remuneration,
    salario,
    school,
    educ,
    vacancyId,
    attachedFile,
    availableTime,
    socialNetworks
  ].some((element) => element === 1 || element === 2);

  const hasQuestions = questions.length !== 0;
  const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);

  return (
    <>
      {/* ESTILOS GENERALES */}
      <GeneralContainer backgroundColor={darkMode ? '#3B3F51' : theme.tertiaryColor} />

      {/* MODAL LOGIN */}
      {showModalLogin.show && (
        <ModalLogin setShowModal={setShowModalLogin} dataModal={showModalLogin} />
      )}
      {showModalSuport && (
        <ModalSupport
          setShowModalSuport={setShowModalSuport}
          setShowModalFaqs={setShowModalFaqs}
          onShowToast={onShowToast}
          formState={formState}
          getValues={getValues}
          cloudflareRecaptcha={cloudflareRecaptcha}
        />
      )}
      {showMiniModalSuport && (
        <MiniModalSupport
          setShowModalShowErrors={setShowModalShowErrors}
          setShowMiniModalSuport={setShowMiniModalSuport}
          trigger={trigger}
        />
      )}
      {showModalFaqs && (
        <ModalFaqs
          setShowModalShowErrors={setShowModalShowErrors}
          setShowModalFaqs={setShowModalFaqs}
          trigger={trigger}
        />
      )}
      {showModalUpdateProfile && (
        <UpdateProfile
          setShowModal={setShowModalUpdateProfile}
          vacancy={vacancy}
          mail={getValues('email')}
          setShowModalUpdateProfileSuccessful={setShowModalUpdateProfileSuccessful}
        />
      )}
      {showModalUpdateProfileSuccessful && (
        <SuccessfulUpdate setShowModal={setShowModalUpdateProfileSuccessful} vacancy={vacancy} />
      )}
      {showModalUpdateProfileApplication && (
        <SuccessfulApplication
          setShowModal={setShowModalUpdateProfileApplication}
          vacancy={vacancy}
        />
      )}
      {showModalExit && <ConfirmExit setShowModal={setShowModalExit} vacancy={vacancy} />}
      {showModalShowErrors && (
        <ModalShowErrors
          setShowModalShowErrors={setShowModalShowErrors}
          setShowModalSuport={setShowModalSuport}
          errrosForm={errors}
        />
      )}

      {/* HEADER */}
      <Header
        theme={theme}
        maxWidth={maxWidth}
        setShowModalFaqs={setShowModalFaqs}
        setShowModalUpdateProfile={setShowModalUpdateProfile}
        vacancy={vacancy}
        key='header-nav'
      />

      {/* PORTADA */}
      <HrImageContainer borderRadius='0px' height='180px'>
        <img src={getDataPostulant.imgBanner} alt='Portada' />
      </HrImageContainer>

      {/* INFORMACION DE LA APLICACION */}
      <ApplicationInformation theme={theme} maxWidth={maxWidth} vacancy={vacancy} />

      {/* tabs */}
      <Tabs maxWidth={maxWidth} arrRequeriment={arrRequeriment} hasQuestions={hasQuestions} />

      {/* BODY */}
      <form key='formVacancy' name='formVacancy'>
        <HrFlexContainer justifyContent='center'>
          <HrFlexContainer
            maxWidth={maxWidth}
            backgroundColor={theme.secondaryColor}
            margin='0px 10px'
            padding='16px 0px 32px 0px'
            flexDirection='column'
            gap='48px'
            alignItems='flex-start'
            background={darkMode ? '#1A1D2B' : ''}
          >
            <HrFlexContainer gap='8px'>
              {(currentStep === 2 || currentStep === 3) && (
                <Flex justifyContent='flex-end'>
                  <HrTitleH6
                    fontSizeSetting={fontSizeSetting}
                    isRequired
                    color={darkMode ? '#DADADA' : '#767676'}
                  >
                    {t('postulant.requiredFields')}
                  </HrTitleH6>
                </Flex>
              )}
              <StepImportCv
                show={currentStep === 1}
                showModalLogin={showModalLogin}
                setShowModalLogin={setShowModalLogin}
                setValue={setValue}
              />
              <StepPersonalInformation
                register={register}
                unregister={unregister}
                formState={formState}
                control={control}
                postulant={postulant}
                // vacancyRequirement={vacancyRequirement}
                getValues={getValues}
                setValue={setValue}
                loading={getPostulantExternal.isLoading}
                setError={setError}
                clearErrors={clearErrors}
                // vacancy={vacancy}
                show={currentStep === 2}
                setShowModalUpdateProfile={setShowModalUpdateProfile}
                trigger={trigger}
              />
              <StepEducationWorkExperience
                show={currentStep === 3}
                register={register}
                formState={formState}
                control={control}
                postulant={postulant}
                getValues={getValues}
                setValue={setValue}
                vacancyRequirement={vacancyRequirement}
                vacancy={vacancy}
                questions={questions}
                unregister={unregister}
                setError={setError}
                clearErrors={clearErrors}
                trigger={trigger}
              />
            </HrFlexContainer>

            {!getPostulantExternal.isLoading && (
              <Flex
                className='desktop'
                justifyContent={vacancy.status !== 6 ? 'space-between' : 'flex-end'}
              >
                {vacancy.status !== 6 && (
                  <HrButtons
                    className='postulation-cancel'
                    type='button'
                    category='cancel'
                    size='large'
                    style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                    onClick={() => {
                      setShowModalExit(true);
                    }}
                  >
                    {t(`general.button.cancel`)}
                  </HrButtons>
                )}
                <HrFlexContainer width='auto'>
                  {currentStep > 1 && (
                    <HrButtons
                      className='postulation-previous-step'
                      type='button'
                      category={darkMode ? 'tertiaryGreenDark' : 'tertiaryGreen'}
                      size='large'
                      style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                      onClick={_prev}
                    >
                      <HrIconButton className='hr-Carret-left' color={HrPrimary} />
                      {t(`general.button.previous`)}
                    </HrButtons>
                  )}

                  {currentStep === 2 && (arrRequeriment || hasQuestions) && (
                    <HrButtons
                      className='postulation-next-step'
                      type='button'
                      category={darkMode ? 'tertiaryGreenDark' : 'tertiaryGreen'}
                      size='large'
                      style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                      onClick={_next}
                    >
                      {t(`general.button.continue`)}
                      <HrIconButton className='hr-Carret-right' color={HrPrimary} />
                    </HrButtons>
                  )}

                  {(currentStep === 3 ||
                    (currentStep === 2 && !arrRequeriment && !hasQuestions)) && (
                    <HrButton
                      className='postulation-create-profile'
                      type='button'
                      name='submit'
                      category='primary'
                      size='large'
                      text='Enviar postulación'
                      fontSize={`${16 + fontSizeSetting || 0}px`}
                      onClick={(e) => {
                        handleSubmit(clickHandlerExecuteRecaptcha, toastError)(e);
                      }}
                      iconColor={HrWhite}
                      loading={isFormSubmitPending(
                        createPostulation.isLoading,
                        isSubmitting,
                        isPendingRecaptchaGeneration
                      )}
                      disabled={isFormSubmitPending(
                        createPostulation.isLoading,
                        isSubmitting,
                        isPendingRecaptchaGeneration
                      )}
                    />
                  )}
                </HrFlexContainer>
              </Flex>
            )}

            {!getPostulantExternal.isLoading && (
              <Flex className='mobile'>
                <HrFlexContainer>
                  {(currentStep === 3 ||
                    (currentStep === 2 && !arrRequeriment && !hasQuestions)) && (
                    <HrButton
                      className='postulation-create-profile'
                      type='button'
                      name='submit'
                      category='primary'
                      size='large'
                      text='Enviar postulación'
                      fontSize={`${16 + fontSizeSetting || 0}px`}
                      onClick={handleSubmit(clickHandlerExecuteRecaptcha, toastError)}
                      iconColor={HrWhite}
                      width='100%'
                      loading={isFormSubmitPending(
                        createPostulation.isLoading,
                        isSubmitting,
                        isPendingRecaptchaGeneration
                      )}
                      disabled={isFormSubmitPending(
                        createPostulation.isLoading,
                        isSubmitting,
                        isPendingRecaptchaGeneration
                      )}
                    />
                  )}
                  {currentStep === 2 && (arrRequeriment || hasQuestions) && (
                    <HrButtons
                      className='postulation-next-step'
                      type='button'
                      category={darkMode ? 'tertiaryGreenDark' : 'tertiaryGreen'}
                      style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                      size='large'
                      onClick={_next}
                      width='100%'
                    >
                      {t(`general.button.continue`)}
                      <HrIconButton className='hr-Carret-right' color={HrPrimary} />
                    </HrButtons>
                  )}
                </HrFlexContainer>
                <HrFlexContainer flexWrap='nowrap'>
                  {vacancy.status !== 6 && (
                    <HrButtons
                      className='postulation-cancel'
                      type='button'
                      category='cancel'
                      style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                      size='large'
                      width='100%'
                      onClick={() => {
                        setShowModalExit(true);
                      }}
                    >
                      {t(`general.button.cancel`)}
                    </HrButtons>
                  )}
                  {currentStep > 1 && (
                    <HrButtons
                      className='postulation-previous-step'
                      type='button'
                      category={darkMode ? 'tertiaryGreenDark' : 'tertiaryGreen'}
                      style={{ fontSize: `${16 + fontSizeSetting || 0}px` }}
                      size='large'
                      width='100%'
                      onClick={_prev}
                    >
                      <HrIconButton className='hr-Carret-left' color={HrPrimary} />
                      {t(`general.button.previous`)}
                    </HrButtons>
                  )}
                </HrFlexContainer>
              </Flex>
            )}
          </HrFlexContainer>
        </HrFlexContainer>
        {cloudflareRecaptcha && (
          <Turnstile
            sitekey={cloudflareRecaptcha}
            theme='auto'
            size='normal'
            onSuccess={(token) => {
              setValue('cloudflare_recaptcha_token', token);
              clearErrors('cloudflare_recaptcha_token');
            }}
            onError={() => {
              setToastState({
                visible: true,
                type: 'error',
                message: t(`postulant.error.invalidRecaptcha`)
              });
              setError('cloudflare_recaptcha_token', {
                type: 'custom',
                message:
                  'Es posible que Cloudflare esté bloqueado en su red. Le recomendamos contactar a su administrador de sistemas o a su proveedor de servicios de Internet para resolver este inconveniente.'
              });
              resetWidgets();
            }}
            onExpire={() => resetWidgets()}
            onVerify={() => {
              setIsPendingRecaptchaGeneration(false);
            }}
            fixedSize
            style={{
              position: 'fixed',
              bottom: '16px',
              right: '16px',
              zIndex: 1000,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px'
            }}
          />
        )}
      </form>

      {toastState.visible && (
        <Toast
          text={toastState.message}
          isVisible={toastState.visible}
          type={toastState.type}
          onClose={() => onCloseToast()}
          msDelay={6000}
        />
      )}

      <HrFlexContainer justifyContent='center' padding='32px 0' gap='8px'>
        <HrParagraph fontSizeSetting={fontSizeSetting} color={HrDarkGrey}>
          Powered by
        </HrParagraph>
        <img
          height='30px'
          src={
            darkMode
              ? '../../../../assets/img/app/HiringRoom-logo_footer_negativo.png'
              : '../../../../assets/img/app/HiringRoom-logo_footer.png'
          }
          alt='Logo HiringRoom'
        />
      </HrFlexContainer>
    </>
  );
});
